<docs>
A panel that slides in from the left or right edge of the viewport (Reka Drawer). The header holds
the optional `icon`, the `title` (or `#title`), `#actions` and the pinned ×; the body is the default
slot, scrolling and carrying no padding of its own (the caller supplies it, `<div class="p-4">…`, as
with Zone::Drawer's `content`); the `#footer` is pinned under the body and renders only when used.
ZoneModal is the centred dialog that blocks the page, ZonePopover a small panel anchored to its
trigger, ZoneFloatingMenu a menu of actions.

## v-model

Optional, and about the open state (Boolean). Leave it off and the drawer owns its own state
(`defaultOpen` is Zone::Drawer's `show:`), the `#trigger`-slot form. Pass it, even as `false`, and
the drawer is fully controlled: the trigger only emits, and nothing opens or closes until you write
the prop back. Same contract as ZoneModal.

## Notes

- Reka's Drawer is alpha. Its swipe-to-dismiss cannot be switched off (only gated behind
  `dismissible`), and its part names/props may change; this wrapper is the single place to absorb
  that.
- Swipe: dragging the panel toward its own edge past ~40px dismisses it, with a mouse as well as a
  finger. Reka ignores drags that start on a button/a/input/select/textarea/label/[role=button], but
  a drag starting on plain text in the body still counts, so text selection inside the panel can
  close it. If that bites, `:dismissible="false"` blocks swipe dismissal too (this wrapper checks
  the close `reason`).
- The drawer teleports to `#zone-teleport` (carries `.w-tw`), so it escapes any overflow/clipping
  ancestor. In tests the zone spec setup stubs Teleport to render in place, so
  `wrapper.find('[role="dialog"]')` works; in the app, query `document.body`. Content only exists
  while open: assert on the trigger, or open it first.
- The slide/fade are CSS animations in this file's `<style>` block, not transitions: Reka's Presence
  reads `animation-name` to decide how long to keep the panel mounted, so a transition would be cut
  off by the unmount. `prefers-reduced-motion` drops both.
- The title is always rendered as an `<h2>`: visibly when `title`/`#title` is set, `sr-only` with
  `ariaLabel` otherwise. Reka needs it for `aria-labelledby` and warns to the console without one;
  the zone spec setup turns that warning into a test failure. `aria-describedby` is dropped entirely
  when there is no description.
- Deliberate deviations from Zone::Drawer (Ruby): `modal` defaults to `true`, so there is now a
  scrim, a scroll lock and a focus trap where Alpine had none (pass `:modal="false"` for the old
  behaviour). The old `min-w-[340px]` is gone, so a 320px-wide screen gets a 320px drawer instead of
  one overflowing the viewport. The Alpine `pointerEvents` setTimeout hack is gone too; Reka's
  Presence handles it.
- No z-index beyond the `z-50` every teleported Zone overlay uses, so paint order inside
  `#zone-teleport` follows mount order: a ZoneSelect or ZonePopover opened from inside the drawer
  renders above it.
</docs>

<template>
  <DrawerRoot
    :open="isOpen"
    :modal="modal"
    :swipe-direction="position"
    @update:open="onOpenChange"
  >
    <DrawerTrigger
      v-if="$slots.trigger"
      as-child
    >
      <slot
        name="trigger"
        :open="isOpen"
      />
    </DrawerTrigger>

    <!--
      Target `#zone-teleport` (defined in the app layouts, see application.scss) rather than the
      reka-ui default of `document.body`. It already carries `.w-tw` — required for Tailwind
      utilities to apply at all, since `important: '.w-tw'` in tailwind.config.js makes `.w-tw` a
      required ancestor — plus the fixed positioning and z-index every other piece of floating Zone
      UI in this app relies on.
    -->
    <DrawerPortal to="#zone-teleport">
      <!--
        Unlike ZoneModal, the scrim is a SIBLING of the panel rather than its container: the panel
        is edge-pinned and full-height, so it needs no positioner, and a pointerdown on the scrim
        counts as "outside" for Reka's DismissableLayer either way. Reka renders this part only
        while `modal` is truthy, which is what makes `:modal="false"` scrim-less on its own.
      -->
      <DrawerOverlay
        :class="OVERLAY_CLASSES"
        data-testid="drawer-overlay"
      />

      <DrawerContent
        :class="contentClasses"
        :style="contentStyle"
        v-bind="contentAttrs"
        data-testid="drawer"
        @escape-key-down="onDismissAttempt"
        @interact-outside="onDismissAttempt"
      >
        <div
          :class="panelClasses"
          data-testid="drawer-panel"
        >
          <!--
            `contents` rather than `v-if` on the row: the <h2> has to exist either way (Reka needs
            it for aria-labelledby, and warns to the console without one), so when there is nothing
            visible to show it stays in the tree as an `sr-only` title with no header chrome.
          -->
          <div
            :class="hasHeader ? HEADER_CLASSES : 'contents'"
            data-testid="drawer-header"
          >
            <ZoneIcon
              v-if="icon && hasHeader"
              :icon="icon"
              :color="iconColor"
              :shade="iconShade"
              class="shrink-0"
              data-testid="drawer-icon"
            />

            <DrawerTitle
              :class="titleClasses"
              data-testid="drawer-title"
            >
              <slot name="title">{{ titleText }}</slot>
            </DrawerTitle>

            <slot
              name="actions"
              :close="close"
            />

            <DrawerClose
              v-if="closeButton"
              as-child
            >
              <ZoneIconButton
                icon="close"
                size="md"
                variant="ghost"
                color="gray"
                class="ml-auto shrink-0"
                :aria-label="t('close')"
                data-testid="drawer-close"
              />
            </DrawerClose>
          </div>

          <DrawerDescription
            v-if="hasDescription"
            :class="DESCRIPTION_CLASSES"
            data-testid="drawer-description"
          >
            <slot name="description">{{ description }}</slot>
          </DrawerDescription>

          <!--
            The body is the scroll container, so the header and footer stay put without needing
            Zone::Drawer's `sticky top-0` header. It carries no padding: consumers wrap their own
            content (the Ruby component's `content` slot behaved the same way).
          -->
          <div
            :class="BODY_CLASSES"
            data-testid="drawer-body"
          >
            <slot :close="close" />
          </div>

          <div
            v-if="$slots.footer"
            :class="FOOTER_CLASSES"
            data-testid="drawer-footer"
          >
            <slot
              name="footer"
              :close="close"
            />
          </div>
        </div>
      </DrawerContent>
    </DrawerPortal>
  </DrawerRoot>
</template>

<script lang="ts">
import { computed, ref, type CSSProperties, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerOverlay,
  DrawerPortal,
  DrawerRoot,
  DrawerTitle,
  DrawerTrigger,
} from 'reka-ui';
import ZoneIcon, { type IconColor, type IconName, type IconShade } from './Icon.vue';
import ZoneIconButton from './IconButton.vue';

export type DrawerPosition = 'left' | 'right';
export type DrawerSize = 'md' | 'lg' | 'xl';
export type DrawerModal = boolean | 'trap-focus';

const POSITIONS: DrawerPosition[] = ['left', 'right'];
const SIZES: DrawerSize[] = ['md', 'lg', 'xl'];

// Zone::Drawer::SIZE_MAPPING, unchanged. MAX widths — the panel is w-full underneath, so a narrow
// screen simply gets a narrower drawer (the Ruby component's `min-w-[340px]` is deliberately gone,
// see the NOTES block).
const SIZE_CLASSES: Record<DrawerSize, string> = {
  md: 'max-w-[340px]',
  lg: 'max-w-[440px]',
  xl: 'max-w-[540px]',
};

const POSITION_CLASSES: Record<DrawerPosition, string> = {
  left: 'left-0',
  right: 'right-0',
};

// The reasons Reka reports for a close it initiated on the user's behalf. `dismissible: false`
// blocks exactly these; 'close-press' (the × / DrawerClose) and 'trigger-press' always go through,
// as does a programmatic close with no reason at all.
const DISMISS_REASONS = ['outside-press', 'escape-key', 'swipe'];

// No z-index beyond the z-50 every teleported Zone overlay uses, so paint order inside
// #zone-teleport follows mount order — see the NOTES block above.
const OVERLAY_CLASSES = 'fixed inset-0 z-50 bg-gray-500/40';

// The edge-pinned positioner.
const CONTENT_CLASSES = 'fixed top-0 z-50 h-full w-full focus:outline-none';

// Zone::Drawer's panel, minus the `overflow-y-auto` (the body scrolls instead) and plus
// `overflow-hidden`, which is what keeps `rounded-xl` clipping the header and footer.
const PANEL_CLASSES =
  'relative flex h-full w-full flex-col overflow-hidden bg-white shadow-xl shadow-gray-500/50 ring-1 ring-gray-300/5';

const HEADER_CLASSES = 'flex shrink-0 items-center gap-1 p-4';

const TITLE_CLASSES = 'min-w-0 flex-1 truncate text-md font-semibold text-gray-500';

const DESCRIPTION_CLASSES = 'shrink-0 px-4 pb-2 text-md text-gray-700';

const BODY_CLASSES = 'no-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto';

const FOOTER_CLASSES = 'shrink-0 border-t border-gray-200 p-4';
</script>

<script setup lang="ts">
defineOptions({ name: 'ZoneDrawer' });

const { t } = useI18n();

const props = defineProps({
  /**
   * Open state, as v-model. Left unset (null) the drawer owns its own state; pass it — even as
   * `false` — and it becomes fully controlled, so the trigger only emits and the caller decides.
   * `default: null` rather than `false` is what keeps Vue from boolean-casting an absent prop.
   */
  modelValue: {
    type: Boolean,
    default: null,
  },
  /** Initial open state while uncontrolled — Zone::Drawer's `show:`. */
  defaultOpen: {
    type: Boolean,
    default: false,
  },
  /** Also picks the swipe-to-dismiss direction. */
  position: {
    type: String as PropType<DrawerPosition>,
    default: 'right',
    validator: (value: string) => POSITIONS.includes(value as DrawerPosition),
  },
  /**
   * 340 / 440 / 540px max widths. The panel is `w-full` under that, so a phone gets a full-width
   * drawer.
   */
  size: {
    type: String as PropType<DrawerSize>,
    default: 'md',
    validator: (value: string) => SIZES.includes(value as DrawerSize),
  },
  /**
   * A CSS max-width for the panel (e.g. `'720px'`) instead of `size`, applied inline so a dynamic
   * value is not dropped by the Tailwind JIT (same reasoning as Zone::Modal).
   */
  width: {
    type: String,
    default: null,
  },
  /** Inset the panel from the viewport edges. Pair with `rounded` for a floating card. */
  padded: {
    type: Boolean,
    default: false,
  },
  /** `rounded-xl` corners on the panel. */
  rounded: {
    type: Boolean,
    default: false,
  },
  /** Header text; truncates to one line. */
  title: {
    type: String,
    default: null,
  },
  /** A line under the header, wired to `aria-describedby`. */
  description: {
    type: String,
    default: null,
  },
  /** Accessible name used when there is no visible title. */
  ariaLabel: {
    type: String,
    default: null,
  },
  /** A leading icon before the title — Zone::Drawer's `leading_icon` slot. */
  icon: {
    type: String as PropType<IconName>,
    default: null,
  },
  /** Colour of the leading icon; gray/500 matches the Ruby component. */
  iconColor: {
    type: String as PropType<IconColor>,
    default: 'gray',
  },
  /** Shade of the leading icon. */
  iconShade: {
    type: Number as PropType<IconShade>,
    default: 500,
  },
  /** The × in the header. */
  closeButton: {
    type: Boolean,
    default: true,
  },
  /**
   * Escape, an outside press and a swipe toward the panel's own edge all close it. `false` blocks
   * all three; the × and anything calling `close()` still work.
   */
  dismissible: {
    type: Boolean,
    default: true,
  },
  /**
   * `true`: scrim, body scroll lock, focus trapped, the rest of the page inert (`pointer-events:
   * none`) and hidden from screen readers. `false`: no scrim, no scroll lock, page stays
   * interactive, no focus trap (what Zone::Drawer did). `'trap-focus'`: scrim, scroll lock and
   * focus trap are kept (Reka draws the scrim for any truthy value); it only drops making the page
   * inert and hiding it from screen readers, rarely what you want. Boolean is listed first so a
   * bare `modal` attribute casts to true rather than `''`.
   */
  modal: {
    type: [Boolean, String] as PropType<DrawerModal>,
    default: true,
    validator: (value: unknown) => typeof value === 'boolean' || value === 'trap-focus',
  },
});

const emit = defineEmits<{
  /** The new open state. */
  'update:modelValue': [open: boolean];
  /** It opened (Zone::Drawer's drawer:opened). */
  open: [];
  /** It closed (Zone::Drawer's drawer:closed). */
  close: [];
}>();

const slots = defineSlots<{
  /**
   * The element that opens it. Rendered with Reka's `as-child`, so it must have exactly one root element (a
   * ZoneButton, ZoneIconButton, `<button>`…). Omit it entirely and drive the drawer with v-model.
   */
  trigger?(props: { open: boolean }): any;
  /** The body. Scrolls, and carries no padding of its own. */
  default?(props: { close: () => void }): any;
  /** Replaces the `title` prop's text; the `<h2>` and its styling stay. */
  title?(): any;
  /** Replaces the `description` prop's text. */
  description?(): any;
  /** Extra header content, between the title and the ×. */
  actions?(props: { close: () => void }): any;
  /** A pinned footer under the scrolling body. */
  footer?(props: { close: () => void }): any;
}>();

const uncontrolledOpen = ref(props.defaultOpen);

const isControlled = computed<boolean>(() => props.modelValue !== null);

const isOpen = computed<boolean>(() => (isControlled.value ? props.modelValue : uncontrolledOpen.value));

const hasVisibleTitle = computed<boolean>(() => Boolean(props.title || slots.title));
const ariaLabel = computed<string>(() => props.ariaLabel ?? t('aria_label'));
const titleText = computed<string>(() => (hasVisibleTitle.value ? props.title : ariaLabel.value));

const hasDescription = computed<boolean>(() => Boolean(props.description || slots.description));

const hasHeader = computed<boolean>(() => hasVisibleTitle.value || props.closeButton || Boolean(slots.actions));

const contentClasses = computed<string>(() => {
  const classes = [CONTENT_CLASSES, POSITION_CLASSES[props.position]];

  if (!props.width) {
    classes.push(SIZE_CLASSES[props.size]);
  }

  if (props.padded) {
    classes.push('p-4');
  }

  return classes.join(' ');
});

const contentStyle = computed<CSSProperties>(() => (props.width ? { maxWidth: props.width } : {}));

/**
 * Reka always writes `aria-describedby` from its own generated id, then warns to the console
 * when nothing carries that id. Overriding it with `undefined` is the documented opt-out and
 * leaves the attribute off the element entirely.
 */
const contentAttrs = computed<Record<string, unknown>>(() => (hasDescription.value ? {} : { 'aria-describedby': undefined }));

const panelClasses = computed<string>(() => (props.rounded ? `${PANEL_CLASSES} rounded-xl` : PANEL_CLASSES));

const titleClasses = computed<string>(() => (hasVisibleTitle.value ? TITLE_CLASSES : 'sr-only'));

/**
 * The Root is always given a real boolean for `open`, so it runs controlled and this is the
 * single place the state can change. `details.reason` is how a swipe dismissal gets blocked by
 * `dismissible: false` — a swipe never goes through DismissableLayer, so unlike Escape and
 * outside presses it cannot be stopped with `preventDefault`.
 */
function onOpenChange(value: boolean, details?: { reason?: string }) {
  if (value === isOpen.value) {
    return;
  }

  if (!value && !props.dismissible && DISMISS_REASONS.includes(details?.reason ?? '')) {
    return;
  }

  if (!isControlled.value) {
    uncontrolledOpen.value = value;
  }

  emit('update:modelValue', value);
  emit(value ? 'open' : 'close');
}

/**
 * Reka's DismissableLayer only dismisses when the event it emitted was not default-prevented.
 * `onOpenChange` would swallow these anyway, but preventing them here also stops the layer's
 * own side effects — and keeps the behaviour identical to ZoneModal's `dismissible`.
 */
function onDismissAttempt(event: Event) {
  if (!props.dismissible) {
    event.preventDefault();
  }
}

function close() {
  onOpenChange(false, { reason: 'close-press' });
}
</script>

<i18n lang="json">
{
  "en": {
    "aria_label": "Drawer",
    "close": "Close"
  }
}
</i18n>
